import type {
  MdCase,
  MdCasePromotionState,
  MdCaseSeverity,
  MdCaseSourceType,
  MdCaseStatus,
  MdCaseWorkflowMode,
} from 'features/mdCases/mdCase'
import {
  formatMdCaseDateTimeRange,
  getSeverityRank,
} from 'features/mdCases/mdCaseDisplay'

import { getMdCaseAlertItemsTimeRange } from './mdCaseDetailsHelpers'

export const MD_CASE_ARCHIVED_STATUSES = ['Closed', 'Cleared'] as const
export const MD_CASE_SHELVED_STATUSES = ['Shelved'] as const
export const MD_CASE_PUBLISHED_STATES = ['published', 'unpublished'] as const

export type MdCasesPublishedState = (typeof MD_CASE_PUBLISHED_STATES)[number]

export interface MdCasesListFilters {
  status?: readonly MdCaseStatus[]
  severity?: readonly MdCaseSeverity[]
  workflow_mode?: readonly MdCaseWorkflowMode[]
  source_type?: readonly MdCaseSourceType[]
  promotion_state?: readonly MdCasePromotionState[]
  published_state?: readonly MdCasesPublishedState[]
}

export interface MdCasesListViewModel {
  activeCases: MdCasesListRow[]
  archivedCases: MdCasesListRow[]
  shelvedCases: MdCasesListRow[]
  counts: {
    total: number
    active: number
    archived: number
    shelved: number
  }
}

export interface MdCasesListRow {
  mdCase: MdCase
  isArchived: boolean
  isShelved: boolean
  publishedState: MdCasesPublishedState
  linkedAlertsCount: number
  alertRangeLabel: string | null
  alertStartTimestamp: number | null
  updatedAt: string
  updatedAtTimestamp: number
}

const ARCHIVED_STATUS_SET = new Set<MdCaseStatus>(MD_CASE_ARCHIVED_STATUSES)
const SHELVED_STATUS_SET = new Set<MdCaseStatus>(MD_CASE_SHELVED_STATUSES)

const matchesFilter = <T extends string>(
  value: T | null,
  selectedValues?: readonly T[]
): boolean => {
  if (!selectedValues?.length) {
    return true
  }

  return value !== null && selectedValues.includes(value)
}

const isArchivedCase = (caseEntry: MdCase): boolean =>
  caseEntry.workflow.case_status !== null &&
  ARCHIVED_STATUS_SET.has(caseEntry.workflow.case_status)

const isShelvedCase = (caseEntry: MdCase): boolean =>
  caseEntry.workflow.case_status !== null &&
  SHELVED_STATUS_SET.has(caseEntry.workflow.case_status)

const getPublishedState = (caseEntry: MdCase): MdCasesPublishedState =>
  caseEntry.workflow.published_at !== null ||
  caseEntry.workflow.promotion_state === 'sent_to_site'
    ? 'published'
    : 'unpublished'

const getUpdatedAtTimestamp = (caseEntry: MdCase): number => {
  const timestamp = Date.parse(caseEntry.rawCase.updated_at)
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp
}

const getAlertRangeLabel = (
  alertRange: ReturnType<typeof getMdCaseAlertItemsTimeRange>
): string | null =>
  alertRange
    ? formatMdCaseDateTimeRange(alertRange.start, alertRange.end)
    : null

const getAlertStartTimestamp = (
  alertRange: ReturnType<typeof getMdCaseAlertItemsTimeRange>
): number | null => {
  if (!alertRange) {
    return null
  }

  const timestamp = Date.parse(alertRange.start)
  return Number.isNaN(timestamp) ? null : timestamp
}

const toMdCasesListRow = (mdCase: MdCase): MdCasesListRow => {
  const alertRange = getMdCaseAlertItemsTimeRange(mdCase.alerts.items)

  return {
    mdCase,
    isArchived: isArchivedCase(mdCase),
    isShelved: isShelvedCase(mdCase),
    publishedState: getPublishedState(mdCase),
    linkedAlertsCount: mdCase.alerts.alert_ids.length,
    alertRangeLabel: getAlertRangeLabel(alertRange),
    alertStartTimestamp: getAlertStartTimestamp(alertRange),
    updatedAt: mdCase.rawCase.updated_at,
    updatedAtTimestamp: getUpdatedAtTimestamp(mdCase),
  }
}

const compareMdCasesListRows = (
  left: MdCasesListRow,
  right: MdCasesListRow
): number => {
  const archivedComparison = Number(left.isArchived) - Number(right.isArchived)
  if (archivedComparison !== 0) {
    return archivedComparison
  }

  const leftStatus = left.mdCase.workflow.case_status
  const rightStatus = right.mdCase.workflow.case_status
  const newAlertComparison =
    Number(leftStatus !== 'New Alert') - Number(rightStatus !== 'New Alert')
  if (newAlertComparison !== 0) {
    return newAlertComparison
  }

  const severityComparison =
    getSeverityRank(left.mdCase.workflow.severity) -
    getSeverityRank(right.mdCase.workflow.severity)
  if (severityComparison !== 0) {
    return severityComparison
  }

  const updatedAtComparison = right.updatedAtTimestamp - left.updatedAtTimestamp
  if (updatedAtComparison !== 0) {
    return updatedAtComparison
  }

  const titleComparison = (left.mdCase.meta.title ?? '').localeCompare(
    right.mdCase.meta.title ?? ''
  )
  if (titleComparison !== 0) {
    return titleComparison
  }

  return left.mdCase.id.localeCompare(right.mdCase.id)
}

const matchesMdCasesListFilters = (
  caseEntry: MdCase,
  filters: MdCasesListFilters
): boolean =>
  matchesFilter(caseEntry.workflow.case_status, filters.status) &&
  matchesFilter(caseEntry.workflow.severity, filters.severity) &&
  matchesFilter(caseEntry.meta.workflow_mode, filters.workflow_mode) &&
  matchesFilter(caseEntry.meta.source_type, filters.source_type) &&
  matchesFilter(caseEntry.workflow.promotion_state, filters.promotion_state) &&
  matchesFilter(getPublishedState(caseEntry), filters.published_state)

export const buildMdCasesListViewModel = (
  cases: readonly MdCase[],
  filters: MdCasesListFilters
): MdCasesListViewModel => {
  const sortedFilteredRows = cases
    .filter((caseEntry) => matchesMdCasesListFilters(caseEntry, filters))
    .map(toMdCasesListRow)
    .slice()
    .sort(compareMdCasesListRows)

  const activeCases = sortedFilteredRows.filter(
    (caseRow) => !caseRow.isArchived && !caseRow.isShelved
  )
  const archivedCases = sortedFilteredRows.filter(
    (caseRow) => caseRow.isArchived
  )
  const shelvedCases = sortedFilteredRows.filter((caseRow) => caseRow.isShelved)

  return {
    activeCases,
    archivedCases,
    shelvedCases,
    counts: {
      total: sortedFilteredRows.length,
      active: activeCases.length,
      archived: archivedCases.length,
      shelved: shelvedCases.length,
    },
  }
}
